import React, { Suspense, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags';
import { Flex } from '@atlaskit/primitives/compiled';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { WorkspacePermissionsProvider } from '../../../../controllers/WorkspacePermissionsProvider';
import { useLegacyWorkspacePermissions } from '../../../../services/workspace-permissions';
import type { Build } from '../../../../types';
import { getWorkspaceIdFromUrl } from '../../../../utils';
import { ActionButton } from '../../../card-footer/action-button';
import { useSolutionPlan } from '../../../solution-plan-context';
import { useHandoffAction } from '../../../utils/use-handoff-action';
import { messages as commonMessages } from '../../messages';

import { messages } from './messages';

type AppFooterActionsInnerProps = {
	build: Build;
	canManageApp: boolean;
	loadingPermissions: boolean;
};

const AppFooterActionsInner = ({
	build,
	canManageApp,
	loadingPermissions,
}: AppFooterActionsInnerProps): React.JSX.Element => {
	di(useHandoffAction);

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const additionalAnalyticsAttributesEnabled = fg('additional-analytics-params-for-sa');

	const { isSubmitting: isSubmittingForBuild, startHandoff: startHandoffForBuild } =
		useHandoffAction(build, {
			handoffPayloadDataFactory: (solutionBuildResponse) => ({
				ari: solutionBuildResponse.ari!,
				type: 'ECOSYSTEM_APP',
				conversationId: build.conversationId,
				appBuilderAutoStart: true,
			}),
		});

	const { isSubmitting: isSubmittingForReview, startHandoff: startHandoffForReview } =
		useHandoffAction(build, {
			handoffPayloadDataFactory: (solutionBuildResponse) => ({
				ari: solutionBuildResponse.ari!,
				type: 'ECOSYSTEM_APP',
				conversationId: build.conversationId,
				appBuilderAutoStart: false,
			}),
		});
	const handleClickBuild = () => {
		startHandoffForBuild();
		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'appSolutionPlanDraft',
			attributes: {
				buildType: build.type,
				buildNumber: build.number,
				buildState: build.state,
				solutionId: build.solutionId,
				solutionVersion: build.solutionVersion,
				conversationId: build.conversationId,
				...(additionalAnalyticsAttributesEnabled && { draftBuildId: build.id }),
			},
		});
	};

	const handleClickReview = () => {
		startHandoffForReview();
		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'appSolutionPlanReviewSpec',
			attributes: {
				buildType: build.type,
				buildNumber: build.number,
				buildState: build.state,
				solutionId: build.solutionId,
				solutionVersion: build.solutionVersion,
				conversationId: build.conversationId,
				...(additionalAnalyticsAttributesEnabled && { draftBuildId: build.id }),
			},
		});
	};

	const isLoadingBuildApp = loadingPermissions || isSubmittingForBuild;
	const isLoadingReviewSpecs = loadingPermissions || isSubmittingForReview;

	const isDisabled = !canManageApp;

	const renderButtons = () => (
		<>
			<ActionButton
				build={build}
				appearance="primary"
				onClick={handleClickBuild}
				isLoading={isLoadingBuildApp}
				isDisabled={isDisabled}
			>
				{formatMessage(messages.buildAppButtonLabel)}
			</ActionButton>
			<ActionButton
				build={build}
				onClick={handleClickReview}
				isLoading={isLoadingReviewSpecs}
				isDisabled={isDisabled}
			>
				{formatMessage(messages.reviewSpecsButtonLabel)}
			</ActionButton>
		</>
	);

	return (
		<Tooltip
			content={formatMessage(commonMessages.permissionDeniedTooltip)}
			canAppear={() => !canManageApp && !loadingPermissions}
		>
			<Flex gap="space.100">{renderButtons()}</Flex>
		</Tooltip>
	);
};

const AppFooterActionsLegacy = ({ build }: { build: Build }): React.JSX.Element => {
	di(getWorkspaceIdFromUrl, useLegacyWorkspacePermissions);

	const workspaceIdFromUrl = useMemo(getWorkspaceIdFromUrl, []) || '';
	const { loading: loadingPermissions, permissions } =
		useLegacyWorkspacePermissions(workspaceIdFromUrl);

	return (
		<AppFooterActionsInner
			build={build}
			canManageApp={permissions.manageApp}
			loadingPermissions={loadingPermissions}
		/>
	);
};

const AppFooterActionsWithQuery = ({ build }: { build: Build }): React.JSX.Element => {
	di(useSolutionPlan, WorkspacePermissionsProvider);

	const { workspaceId, spaceId, aaid } = useSolutionPlan();

	return (
		<WorkspacePermissionsProvider
			workspaceId={workspaceId || ''}
			spaceId={spaceId || ''}
			accountId={aaid}
		>
			{({ permissions }) => (
				<AppFooterActionsInner
					build={build}
					canManageApp={permissions.manageApp}
					loadingPermissions={false}
				/>
			)}
		</WorkspacePermissionsProvider>
	);
};

const AppFooterActionsLoading = ({ build }: { build: Build }): React.JSX.Element => (
	<AppFooterActionsInner build={build} canManageApp={false} loadingPermissions />
);

export const AppFooterActions = ({ build }: { build: Build }): React.JSX.Element => {
	// one flag is dependent on the other and the original flag is in RTs whereas the latter isn't
	// eslint-disable-next-line @atlaskit/platform/no-preconditioning
	return fg('fe_enable_sa_externally') && fg('sltns-1069-popup-blocked-bug-fix') ? (
		<Suspense fallback={<AppFooterActionsLoading build={build} />}>
			<AppFooterActionsWithQuery build={build} />
		</Suspense>
	) : (
		<AppFooterActionsLegacy build={build} />
	);
};
